import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Compass, Plane, Box, ShieldCheck, FileText, Home, Glasses, ArrowRight } from 'lucide-react';
import { services } from '../mock';

const iconMap = {
  Compass,
  Plane,
  Box,
  ShieldCheck,
  FileText,
  Home,
  Glasses
};

const Services = () => {
  return (
    <div className="bg-[#0A1628] min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="inline-block px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full text-[#D4AF37] text-sm font-semibold mb-4"
          >
            Servizi Professionali
          </motion.span>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Soluzioni <span className="text-[#D4AF37]">Tecniche Avanzate</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Offriamo servizi completi di topografia, aerofotogrammetria, modellazione BIM e coordinamento sicurezza cantieri con tecnologie all'avanguardia.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="space-y-8">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group"
              >
                <div className="relative bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-3xl overflow-hidden border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-500 shadow-2xl hover:shadow-[#D4AF37]/10">
                  <div className="grid md:grid-cols-12 gap-0">
                    {/* Desktop Number Badge - hidden on mobile */}
                    <div className="hidden md:flex md:col-span-2 bg-gradient-to-br from-[#D4AF37] to-[#C5A028] items-center justify-center p-8 md:p-12">
                      <motion.span
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        className="text-6xl md:text-7xl font-bold text-[#0A1628] opacity-90"
                      >
                        {service.number}
                      </motion.span>
                    </div>

                    {/* Content */}
                    <div className="md:col-span-10 p-6 md:p-12">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
                        <div className="flex-1">
                          {/* Mobile: number + icon + title inline */}
                          <div className="flex items-center gap-3 mb-4">
                            {/* Number badge inline (mobile only) */}
                            <div className="md:hidden w-11 h-11 bg-gradient-to-br from-[#D4AF37] to-[#C5A028] rounded-lg flex items-center justify-center flex-shrink-0 shadow-md">
                              <span className="text-lg font-bold text-[#0A1628]">{service.number}</span>
                            </div>
                            {/* Icon (all breakpoints) */}
                            <div className="w-11 h-11 md:w-14 md:h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                              <IconComponent className="w-5 h-5 md:w-7 md:h-7 text-[#D4AF37]" />
                            </div>
                            <h2 className="text-xl md:text-4xl font-bold text-white group-hover:text-[#D4AF37] transition-colors duration-300 leading-tight">
                              {service.title}
                            </h2>
                          </div>
                          <p className="text-base md:text-lg text-gray-400 mb-6 leading-relaxed">
                            {service.description}
                          </p>
                          <ul className="grid sm:grid-cols-2 gap-3">
                            {service.details.map((detail, idx) => (
                              <li key={idx} className="flex items-start space-x-3">
                                <div className="w-1.5 h-1.5 bg-[#D4AF37] rounded-full mt-2 flex-shrink-0" />
                                <span className="text-gray-500 text-sm">{detail}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <motion.div
                          whileHover={{ x: 5 }}
                          className="flex-shrink-0"
                        >
                          <Link
                            to="/contact"
                            className="inline-flex items-center space-x-2 px-6 py-3 bg-white/5 hover:bg-[#D4AF37] border border-white/10 hover:border-[#D4AF37] rounded-full text-white hover:text-[#0A1628] font-medium transition-all duration-300 group/btn"
                          >
                            <span>Richiedi Info</span>
                            <ArrowRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform duration-300" />
                          </Link>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-br from-[#1A2B47] to-[#0A1628] rounded-3xl p-12 md:p-16 border border-[#D4AF37]/20 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.1),transparent_70%)]" />
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                Hai bisogno di un <span className="text-[#D4AF37]">preventivo</span>?
              </h2>
              <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
                Contattaci per una consulenza gratuita e un preventivo personalizzato per il tuo progetto.
              </p>
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: '0 0 30px rgba(212,175,55,0.4)' }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center space-x-2 px-8 py-4 bg-gradient-to-r from-[#D4AF37] to-[#C5A028] text-[#0A1628] rounded-full font-semibold text-lg shadow-lg hover:shadow-2xl transition-all duration-300"
                >
                  <span>Contattaci Ora</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Services;
